import './neb-booking-service-item';
import '../../../neb-styles/neb-icons';
import '../../../neb-lit-components/src/components/inputs/neb-select';
import equal from 'fast-deep-equal';
import { LitElement, html, css } from 'lit';

import { navigate } from '../../../../src/store';
import { getAppointmentTypeLocationPublic } from '../../../neb-api-client/src/appointment-types';
import { store, connect } from '../../../neb-redux/neb-redux-store';
import { baseStyles } from '../../../neb-styles/neb-styles';
import {
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_WEIGHT_BOLD,
  CSS_COLOR_GREY_2,
} from '../../../neb-styles/neb-variables';
import { fetchServices } from '../store/services/servicesAction';

import { getAvailableProviders } from './util/availability-util';
import { getBookingWalkInOverridesV2 } from './util/enable-booking-util';

export const ELEMENTS = {
  locationTitle: { id: 'title-location' },
  locationContainer: { id: 'container-location' },
  locationSelect: { id: 'dropdown-select' },
  locationAddress: { id: 'address-location' },
  servicesContainer: { id: 'container-services' },
  serviceItems: { selector: '.service-item', tag: 'neb-booking-service-item' },
  noServicesMessagesContainer: { id: 'container-no-services' },
};

export const EMPTY_LOCATION = {
  label: '',
  allowOnlineBooking: true,
  allowWalkInAppointments: true,
};

class NebBookingServices extends connect(store)(LitElement) {
  static get properties() {
    return {
      __locationError: String,
      __appointmentTypeLocation: Array,
      __services: Array,
      __filteredServices: Array,
      __filteredLocations: Array,
      __selectedLocation: Object,

      layout: {
        type: String,
        reflect: true,
      },
      locations: Array,
      appointment: Object,
      isWalkIn: Boolean,
    };
  }

  static get styles() {
    return [
      baseStyles,
      css`
        .title,
        .no-services {
          color: ${CSS_COLOR_HIGHLIGHT};
          font-size: 16px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          padding: 30px 25px 0;
        }

        .container-location {
          padding: 30px 25px 0 25px;
        }

        :host([layout='small']) .container-location {
          padding: 0 25px 0 25px;
        }

        .dropdown-location {
          width: 50%;
        }

        :host([layout='small']) .dropdown-location {
          width: 100%;
        }

        .container-address {
          padding-top: 20px;
        }

        .helper-address {
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .address {
          cursor: pointer;
          text-decoration: underline;
        }

        .no-services {
          font-size: 14px;
        }

        :host([layout='small']) .title {
          padding: 30px 25px;
        }

        .service-item:not(:first-child) {
          border-top: 1px solid ${CSS_COLOR_GREY_2};
        }

        :host([layout='small']) .service-item:first-child {
          border-top: 1px solid ${CSS_COLOR_GREY_2};
        }
      `,
    ];
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.__locationError = '';
    this.__appointmentTypeLocation = [];
    this.__services = [];
    this.__filteredServices = [];
    this.__filteredLocations = [];
    this.__selectedLocation = EMPTY_LOCATION;

    this.layout = '';
    this.locations = [EMPTY_LOCATION];
    this.appointment = {};

    this.onServiceSelected = () => {};
  }

  __initHandlers() {
    this.__handlers = {
      openMap: () => {
        const mapUrl = `https://www.google.com/maps/place/${this.__formatAddress(
          this.__selectedLocation,
        )}`;
        window.open(mapUrl, '_blank');
      },
      renderLocationItem: item => {
        const addressStyling =
          this.layout === 'small'
            ? 'width: 315px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
            : '';
        return html`
          ${item.id
            ? html`
                <div
                  style="display: grid; grid-template-rows: 1fr 1fr; padding: 0 20px;"
                >
                  <div style="font-weight: bold;">${item.label}</div>

                  <div style="${addressStyling}">
                    ${this.__formatAddress(item)}
                  </div>
                </div>
              `
            : html` <div style="padding: 20px;">${item.label}</div> `}
        `;
      },
      selectLocation: ({ value }) => {
        this.__selectedLocation = value;
        this.__locationError = '';
      },
      selectServiceItem: service => {
        if (this.isWalkIn && !this.__selectedLocation.id) {
          this.__locationError = 'Please select a location';

          return;
        }

        const locationId = this.__selectedLocation.id;

        this.onServiceSelected(service, locationId);
      },
    };
  }

  updated(changedProps) {
    if (
      changedProps.has('appointment') &&
      this.appointment.locationId &&
      this.locations.length
    ) {
      this.__selectedLocation = this.locations.find(
        l => l.id === this.appointment.locationId,
      );
    }

    if (changedProps.has('locations')) {
      this.__filteredLocations = this.locations.filter(loc =>
        this.isWalkIn ? loc.allowWalkInAppointments : loc.allowOnlineBooking,
      );
    }
  }

  async firstUpdated() {
    const { enableBooking, enableWalkIn } = await getBookingWalkInOverridesV2(
      store.getState(),
    );

    if (
      (this.isWalkIn && !enableWalkIn) ||
      (!this.isWalkIn && !enableBooking)
    ) {
      navigate('#/');
    }
  }

  connectedCallback() {
    super.connectedCallback();
    return store.dispatch(fetchServices());
  }

  async _stateChanged(state) {
    if (
      !equal(state.services && state.services.serviceItems !== this.__services)
    ) {
      const {
        providers: { item: providers },
        providersAvailability: { item: providersAvailability },
        services: { serviceItems },
        booking: { tenantShortName },
      } = state;

      if (providersAvailability && providers) {
        const servicesWithAvailableProviders = serviceItems.filter(service => {
          const availableProviders = getAvailableProviders(
            providers,
            providersAvailability,
            service.id,
          );
          return availableProviders.length;
        });

        if (!equal(this.__filteredServices, servicesWithAvailableProviders)) {
          this.__appointmentTypeLocation =
            await getAppointmentTypeLocationPublic(tenantShortName);

          this.__filteredServices = servicesWithAvailableProviders;

          this.__services = this.__filteredServices
            .filter(service => this.__hasValidLocation(service))
            .filter(service =>
              this.isWalkIn ? service.walkInEnabled : service.visibleOnline,
            );
        }
      }
    }
  }

  __isDisabledService(item) {
    if (this.__selectedLocation) {
      return this.__selectedLocation.id
        ? !this.__appointmentTypeLocation.some(
            relation =>
              relation.appointmentTypeId === item.id &&
              relation.locationId === this.__selectedLocation.id,
          )
        : false;
    }

    return true;
  }

  __hasValidLocation(service) {
    return this.__appointmentTypeLocation.some(
      relation =>
        relation.appointmentTypeId === service.id &&
        this.__filteredLocations.some(
          location => location.id === relation.locationId,
        ),
    );
  }

  __formatAddress(address) {
    if (address) {
      const { address1, address2, city, state, zipcode, zipCode } = address;
      return [address1, address2, city, state, zipcode || zipCode]
        .filter(item => item)
        .join(', ');
    }

    return '';
  }

  __renderAddress() {
    return this.__selectedLocation && this.__selectedLocation.id
      ? html`
          <div class="container-address">
            <div class="helper-address">Click to view in maps:</div>
            <div
              id="${ELEMENTS.locationAddress.id}"
              class="address"
              @click="${this.__handlers.openMap}"
            >
              ${this.__formatAddress(this.__selectedLocation)}
            </div>
          </div>
        `
      : '';
  }

  __renderLocations() {
    const itemHeight = this.layout === 'small' ? '80' : '65';
    return html`
      <div class="title" id="${ELEMENTS.locationTitle.id}">Select Location</div>
      <div id="${ELEMENTS.locationContainer.id}" class="container-location">
        <neb-select
          id="${ELEMENTS.locationSelect.id}"
          class="dropdown-location"
          label="Location"
          helper="${this.isWalkIn ? 'Required' : ''}"
          error="${this.__locationError}"
          itemHeight="${itemHeight}"
          .value="${this.__selectedLocation}"
          .items="${this.__filteredLocations}"
          .forceDown="${true}"
          .showFullText="${this.layout !== 'small'}"
          .onChange="${this.__handlers.selectLocation}"
          .onRenderItem="${this.__handlers.renderLocationItem}"
        ></neb-select>
        ${this.__renderAddress()}
      </div>
    `;
  }

  __renderNoServicesMessage() {
    return html`
      <div id="${ELEMENTS.noServicesMessagesContainer.id}" class="no-services">
        <p>
          The practice is not accepting online appointment scheduling at this
          time.
        </p>
        <p>
          Please contact the practice directly to schedule your appointment.
        </p>
      </div>
    `;
  }

  __renderContent() {
    return html`
      ${this.__renderLocations()}
      <div class="title">Select Service</div>
      <div id="${ELEMENTS.servicesContainer.id}">
        ${this.__services.map(
          item => html`
            <neb-booking-service-item
              class="service-item"
              service-name="${item.name}"
              .item="${item}"
              .layout="${this.layout}"
              .disabled="${this.__isDisabledService(item)}"
              .onServiceItemSelected="${this.__handlers.selectServiceItem}"
            ></neb-booking-service-item>
          `,
        )}
      </div>
    `;
  }

  render() {
    return this.__services.length > 0
      ? this.__renderContent()
      : this.__renderNoServicesMessage();
  }
}

customElements.define('neb-booking-services', NebBookingServices);
